const US_TERM_EPSILON = 1e-8;

export function normalizeEpochSeconds(value) {
  const n = Number(value || 0);

  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }

  return n > 1e12
    ? Math.floor(n / 1000)
    : Math.floor(n);
}

export function coerceEpochSeconds(value) {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return normalizeEpochSeconds(value.getTime());
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Date.parse(trimmed);
    if (Number.isFinite(parsed)) {
      return normalizeEpochSeconds(parsed);
    }

    return normalizeEpochSeconds(trimmed);
  }

  return normalizeEpochSeconds(value);
}

export function calculateHoldingDays({
  acquiredAt,
  disposedAt,
} = {}) {
  const acquiredTs = coerceEpochSeconds(acquiredAt);
  const disposedTs = coerceEpochSeconds(disposedAt);

  if (
    acquiredTs === null ||
    disposedTs === null ||
    disposedTs < acquiredTs
  ) {
    return null;
  }

  const acquiredDate = new Date(acquiredTs * 1000);
  const disposedDate = new Date(disposedTs * 1000);

  const acquiredUtcMidnight = Date.UTC(
    acquiredDate.getUTCFullYear(),
    acquiredDate.getUTCMonth(),
    acquiredDate.getUTCDate()
  );

  const disposedUtcMidnight = Date.UTC(
    disposedDate.getUTCFullYear(),
    disposedDate.getUTCMonth(),
    disposedDate.getUTCDate()
  );

  return Math.floor(
    (disposedUtcMidnight - acquiredUtcMidnight) / 86400000
  );
}

/**
 * US Long-Term: Kalendertage, Tag nach Anschaffung = Tag 1.
 * Long-Term ab dem Tag nach dem 1-Jahres-Jahrestag (>= Start + 1 Jahr).
 */
export function isLongTermHolding(arg1, arg2) {
  const params = (typeof arg1 === 'object' && arg1 !== null && arg2 === undefined)
    ? arg1
    : { acquiredAt: arg1, disposedAt: arg2 };

  const acqSec = coerceEpochSeconds(params.acquiredAt);
  const outSec = coerceEpochSeconds(params.disposedAt);

  if (acqSec == null || outSec == null) {
    return false;
  }

  const acq = new Date(acqSec * 1000);
  const out = new Date(outSec * 1000);

  const start = new Date(Date.UTC(
    acq.getUTCFullYear(),
    acq.getUTCMonth(),
    acq.getUTCDate() + 1
  ));

  const plusOneYear = new Date(start);
  plusOneYear.setUTCFullYear(start.getUTCFullYear() + 1);

  const outNormalized = new Date(Date.UTC(
    out.getUTCFullYear(),
    out.getUTCMonth(),
    out.getUTCDate()
  ));

  return outNormalized.getTime() >= plusOneYear.getTime();
}

export function getUsHoldingTerm({
  acquiredAt,
  disposedAt,
} = {}) {
  return isLongTermHolding({ acquiredAt, disposedAt })
    ? 'LONG_TERM'
    : 'SHORT_TERM';
}

export function resolveLegHoldingDays(leg = {}, disposal = {}) {
  const direct = Number(
    leg.holdingDays ??
    leg.holding_days
  );

  if (Number.isFinite(direct) && direct >= 0) {
    return direct;
  }

  return calculateHoldingDays({
    acquiredAt:
      leg.acquiredAt ??
      leg.acquired_at ??
      leg.acquisitionTs ??
      leg.acquisition_ts ??
      leg.acquiredTs ??
      leg.timestamp ??
      leg.ts,

    disposedAt:
      leg.disposedAt ??
      leg.disposed_at ??
      leg.disposalTs ??
      leg.disposal_ts ??
      disposal.timestamp ??
      disposal.ts,
  });
}

export function aggregateHoldingDaysFromLegs(fifoLegs = [], disposal = {}) {
  const holdingDaysValues = (fifoLegs || [])
    .map((leg) => resolveLegHoldingDays(leg, disposal))
    .filter(
      (value) =>
        Number.isFinite(value) &&
        value >= 0
    );

  return {
    holdingDaysMin:
      holdingDaysValues.length > 0
        ? Math.min(...holdingDaysValues)
        : null,
    holdingDaysMax:
      holdingDaysValues.length > 0
        ? Math.max(...holdingDaysValues)
        : null,
  };
}

export function resolveLegIsLongTerm(leg = {}, disposal = {}) {
  if (leg.isLongTerm === true) {
    return true;
  }

  if (leg.isLongTerm === false) {
    return false;
  }

  return isLongTermHolding({
    acquiredAt:
      leg.acquisitionTs ??
      leg.acquired_at ??
      leg.acquiredAt ??
      leg.timestamp ??
      leg.ts ??
      leg.acquired_ts,
    disposedAt:
      disposal.timestamp ??
      disposal.ts ??
      leg.disposedAt ??
      leg.disposal_ts,
  });
}

export function assertNearlyEqual(
  actual,
  expected,
  label,
  epsilon = US_TERM_EPSILON
) {
  const delta = Math.abs(
    Number(actual || 0) -
    Number(expected || 0)
  );

  if (delta > epsilon) {
    throw new Error(
      `${label}: mismatch ` +
      `actual=${actual} ` +
      `expected=${expected} ` +
      `delta=${delta}`
    );
  }
}

export function summarizeUsTermTotalsFromDisposals(disposalRows = []) {
  let shortTermGainLoss = 0;
  let longTermGainLoss = 0;
  let totalGainLoss = 0;

  for (const row of disposalRows || []) {
    shortTermGainLoss += Number(
      row.shortTermProfitFiat ??
      row.shortTermProfitEUR ??
      0
    );
    longTermGainLoss += Number(
      row.longTermProfitFiat ??
      row.longTermProfitEUR ??
      0
    );
    totalGainLoss += Number(
      row.taxableProfitFiat ??
      row.taxableProfitEUR ??
      row.taxableProfit ??
      0
    );
  }

  return {
    shortTermGainLoss,
    longTermGainLoss,
    totalGainLoss,
  };
}

export function summarizeUsTermTotalsFromForm8949Legs(disposalRows = []) {
  let shortTermGainLoss = 0;
  let longTermGainLoss = 0;
  let totalGainLoss = 0;

  for (const disposal of disposalRows || []) {
    const legs = Array.isArray(disposal?.fifoLegs)
      ? disposal.fifoLegs
      : [];

    for (const leg of legs) {
      if (leg?.isTaxable !== true) {
        continue;
      }

      const gainLoss = Number(
        leg.legProfitFiat ??
        leg.legProfitEUR ??
        leg.profitEUR ??
        leg.gainEUR ??
        0
      );

      if (resolveLegIsLongTerm(leg, disposal)) {
        longTermGainLoss += gainLoss;
      } else {
        shortTermGainLoss += gainLoss;
      }

      totalGainLoss += gainLoss;
    }
  }

  return {
    shortTermGainLoss,
    longTermGainLoss,
    totalGainLoss,
  };
}

export function assertUsDisposalForm8949CrossExport(
  disposalTotals,
  form8949Totals
) {
  assertNearlyEqual(
    disposalTotals.shortTermGainLoss,
    form8949Totals.shortTermGainLoss,
    'US_SHORT_TERM_CROSS_EXPORT'
  );

  assertNearlyEqual(
    disposalTotals.longTermGainLoss,
    form8949Totals.longTermGainLoss,
    'US_LONG_TERM_CROSS_EXPORT'
  );

  assertNearlyEqual(
    disposalTotals.totalGainLoss,
    form8949Totals.totalGainLoss,
    'US_TOTAL_GAIN_CROSS_EXPORT'
  );
}
